import React, { useState, useEffect, useCallback } from 'react';
import {
  getNotificationLists,
  createNotificationList,
  updateNotificationList,
  getListMembers,
  addListMember,
  removeListMember,
  getJobCostRoster,
  saveJobCostRosterEntry,
  archiveJobCostRosterEntry,
  restoreJobCostRosterEntry,
  purgeJobCostRosterEntry,
  pullJobCostRoster,
  pullJobCostRosterOne,
  setJobCostRosterCadence,
  getJobCostBackfill,
  runJobCostBackfill,
  setJobCostBackfillStart,
  getArSyncSettings,
  setArSyncCadence,
  pullArSyncNow,
  getArSyncBackfill,
  runArSyncBackfill,
} from '../api';
import LoadingState from '../components/LoadingState';
import StateScreen from '../components/ui/StateScreen';

/* ═══════════════════════════════════════════════════════════════════════════
   Settings — warm-cream + sage token redesign (single-file page)
   Admin-only container for settings sections (left section rail). Today: one
   section, Notification Lists — manage the email recipient lists that drive
   automated alerts (create, enable/disable, add/remove members). Visual reskin
   only — APIs, admin gating, per-row guards, and auto-select are unchanged.
   Full-width: .app-main supplies the 24px gutter; AppHeader supplies the title.
   ═══════════════════════════════════════════════════════════════════════════ */

/* ─────────────── icons (inline SVG, no glyphs) ─────────────── */
const StIcon = ({ name, size = 16, sw = 1.6 }) => {
  const p = (d) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>{d}</svg>
  );
  switch (name) {
    case 'plus':      return p(<path d="M12 5v14M5 12h14" />);
    case 'x':         return p(<path d="M6 6l12 12M6 18 18 6" />);
    case 'check':     return p(<path d="m5 12 5 5L20 7" />);
    case 'trash':     return p(<><path d="M4 7h16" /><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /><path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" /><path d="M10 11v6M14 11v6" /></>);
    case 'user-plus': return p(<><circle cx="9" cy="8" r="3.3" /><path d="M3.5 20a5.5 5.5 0 0 1 11 0" /><path d="M19 8v6M22 11h-6" /></>);
    case 'power':     return p(<><path d="M12 4v8" /><path d="M7.5 7a7 7 0 1 0 9 0" /></>);
    case 'bell':      return p(<><path d="M6 8a6 6 0 0 1 12 0c0 4 1.5 5 2 6H4c.5-1 2-2 2-6Z" /><path d="M10 19a2 2 0 0 0 4 0" /></>);
    case 'users':     return p(<><path d="M16 19a4 4 0 0 0-8 0" /><circle cx="12" cy="9" r="3" /><path d="M4.5 19a3 3 0 0 1 4-3.2M19.5 19a3 3 0 0 0-4-3.2" /></>);
    case 'alert-tri': return p(<><path d="M10.3 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /><path d="M12 9v4" /><circle cx="12" cy="17" r="0.9" fill="currentColor" stroke="none" /></>);
    case 'briefcase': return p(<><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 12h18" /></>);
    case 'coins':     return p(<><ellipse cx="8" cy="6" rx="5" ry="2.6" /><path d="M3 6v6c0 1.4 2.2 2.6 5 2.6s5-1.2 5-2.6V6" /><path d="M13 10.5c.6 1.6 3 2.6 5.5 2.1" /><ellipse cx="16" cy="14" rx="5" ry="2.6" /><path d="M11 14v4c0 1.4 2.2 2.6 5 2.6s5-1.2 5-2.6v-4" /></>);
    case 'refresh':   return p(<><path d="M21 12a9 9 0 1 1-3-6.7" /><path d="M21 4v5h-5" /></>);
    case 'clock':     return p(<><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>);
    case 'archive':   return p(<><rect x="3" y="4" width="18" height="4" rx="1" /><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8" /><path d="M10 12h4" /></>);
    case 'chevron-d': return p(<path d="m6 9 6 6 6-6" />);
    case 'spin':      return (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
        strokeWidth={sw} strokeLinecap="round" style={{ display: 'block', animation: 'sSpin .8s linear infinite' }}>
        <path d="M21 12a9 9 0 1 1-6.2-8.55" />
      </svg>
    );
    default: return null;
  }
};

/* ─────────────── settings sections (the rail; scalable) ─────────────── */
const SETTINGS_SECTIONS = [
  { id: 'notification-lists', icon: 'bell', label: 'Notification Lists', hint: 'Email recipient lists', available: true },
  { id: 'job-cost-roster', icon: 'briefcase', label: 'Job Cost', hint: 'Pull + backfill job cost', available: true },
  { id: 'ar-sync', icon: 'coins', label: 'AR Sync', hint: 'Pull + backfill AR', available: true },
];

/* ─────────────── formatters ─────────────── */
// created_at is stored UTC; append 'Z' so it parses as UTC. null/'' → null.
const fmtDate = (v) => {
  if (v == null || v === '') return null;
  const s = typeof v === 'string' ? (/(Z|[+-]\d\d:?\d\d)$/.test(v) ? v : v + 'Z') : v;
  const x = s instanceof Date ? s : new Date(s);
  if (isNaN(x)) return null;
  return x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

/* ─────────────── small primitives ─────────────── */
const StatusBadge = ({ active, size = 'md' }) => {
  const h = size === 'sm' ? 20 : 22;
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, height: h, padding: '0 9px 0 8px', borderRadius: 6,
      background: active ? 'color-mix(in oklch, var(--success) 14%, var(--bg-surface))' : 'var(--bg-surface-2)',
      border: '1px solid ' + (active ? 'color-mix(in oklch, var(--success) 22%, transparent)' : 'var(--border-subtle)'),
      fontSize: size === 'sm' ? 11 : 11.5, fontWeight: 600, color: active ? 'var(--success)' : 'var(--text-4)', whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, flex: '0 0 auto', background: active ? 'var(--success)' : 'var(--text-4)' }} />
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

const CountChip = ({ count }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 5, height: 20, padding: '0 8px 0 7px', borderRadius: 999,
    background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', whiteSpace: 'nowrap',
  }}>
    <span style={{ color: 'var(--text-3)', display: 'inline-flex' }}><StIcon name="users" size={11} sw={1.7} /></span>
    <span className="mono" style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>{count || 0}</span>
    <span style={{ fontSize: 12.5, color: 'var(--text-3)', fontWeight: 500 }}>{(count || 0) === 1 ? 'member' : 'members'}</span>
  </span>
);

const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 15px 0 13px', borderRadius: 8,
  fontSize: 14, fontWeight: 600, color: '#fff', whiteSpace: 'nowrap', background: 'var(--accent)',
  border: '1px solid color-mix(in oklch, var(--accent) 50%, oklch(0.3 0.05 135))',
  boxShadow: '0 1px 0 oklch(0.42 0.06 135), 0 1px 2px oklch(0.4 0.05 135 / 0.25)', cursor: 'pointer',
};

/* ─────────────── section rail ─────────────── */
const SectionRail = ({ sections, active, onSelect }) => (
  <nav style={{ width: 220, flex: '0 0 220px', alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 3 }}>
    <div style={{ padding: '2px 6px 8px', fontSize: 12, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-4)' }}>Settings</div>
    {sections.map((sec) => {
      const on = sec.id === active;
      const enabled = sec.available !== false;
      return (
        <button key={sec.id} type="button" disabled={!enabled} onClick={() => enabled && onSelect(sec.id)}
          className={enabled ? 's-rail-item' : ''} style={{
            position: 'relative', display: 'flex', alignItems: 'center', gap: 11,
            minHeight: 44, padding: '8px 12px 8px 13px', borderRadius: 9, textAlign: 'left',
            background: on ? 'var(--accent-soft)' : 'transparent',
            border: '1px solid ' + (on ? 'color-mix(in oklch, var(--accent) 22%, transparent)' : 'transparent'),
            cursor: enabled ? 'pointer' : 'default', opacity: enabled ? 1 : 0.55, overflow: 'hidden',
          }}>
          {on && <span style={{ position: 'absolute', left: 0, top: 8, bottom: 8, width: 3, borderRadius: 999, background: 'var(--accent)' }} />}
          <span style={{
            width: 28, height: 28, borderRadius: 7, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            color: on ? 'var(--accent-text)' : 'var(--text-3)',
            background: on ? 'color-mix(in oklch, var(--accent) 12%, var(--bg-surface))' : 'var(--bg-surface-2)',
            border: '1px solid ' + (on ? 'color-mix(in oklch, var(--accent) 22%, transparent)' : 'var(--border-subtle)'),
          }}><StIcon name={sec.icon} size={15} sw={1.7} /></span>
          <span style={{ display: 'flex', flexDirection: 'column', minWidth: 0, gap: 1 }}>
            <span style={{ fontSize: 14.5, fontWeight: on ? 600 : 500, color: on ? 'var(--accent-text)' : 'var(--text-2)', whiteSpace: 'nowrap', letterSpacing: '-0.005em' }}>{sec.label}</span>
            {sec.hint && <span style={{ fontSize: 12.5, color: 'var(--text-4)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sec.hint}</span>}
          </span>
        </button>
      );
    })}
    <div style={{ marginTop: 8, padding: '10px 12px', borderRadius: 9, border: '1px dashed var(--border-default)', background: 'var(--bg-surface-2)' }}>
      <div style={{ fontSize: 11.5, fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', color: 'var(--text-4)', marginBottom: 4 }}>More coming</div>
      <div style={{ fontSize: 13, color: 'var(--text-4)', lineHeight: 1.45, textWrap: 'pretty' }}>General, Integrations &amp; Billing settings will live here as the workspace grows.</div>
    </div>
  </nav>
);

/* ─────────────── list card ─────────────── */
const ListCard = ({ list, selected, onSelect }) => (
  <button type="button" onClick={() => onSelect(list.id)} className={selected ? '' : 's-listcard'} style={{
    display: 'flex', flexDirection: 'column', gap: 8, width: '100%', textAlign: 'left', padding: '13px 14px', borderRadius: 11,
    background: selected ? 'var(--accent-soft)' : 'var(--bg-surface)',
    border: '1px solid ' + (selected ? 'color-mix(in oklch, var(--accent) 22%, transparent)' : 'var(--border-default)'),
    boxShadow: selected ? 'none' : 'var(--shadow-xs)', cursor: 'pointer', transition: 'background 120ms, border-color 120ms',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span className="mono" style={{
        flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600, letterSpacing: '-0.01em',
        color: selected ? 'var(--accent-text)' : 'var(--text-1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
      }}>{list.list_name}</span>
      <StatusBadge active={!!list.is_active} size="sm" />
    </div>
    {list.description
      ? <div style={{ fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.45, textWrap: 'pretty', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{list.description}</div>
      : <div style={{ fontSize: 13.5, color: 'var(--text-4)', fontStyle: 'italic' }}>No description</div>}
    <div style={{ display: 'flex' }}><CountChip count={list.member_count} /></div>
  </button>
);

/* ─────────────── member table ─────────────── */
const MTh = ({ children, align = 'left', width }) => (
  <th style={{
    textAlign: align, width, padding: '0 16px', height: 36, whiteSpace: 'nowrap',
    background: 'var(--bg-surface-2)', borderBottom: '1px solid var(--border-default)',
    fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)',
  }}>{children}</th>
);

const MemberTable = ({ members, onRemove, removingId }) => {
  const active = (members || []).filter((m) => m.is_active);
  if (active.length === 0) {
    return (
      <div style={{ padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
        <span style={{ width: 44, height: 44, borderRadius: 11, color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <StIcon name="users" size={20} sw={1.6} />
        </span>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>No members in this list yet</div>
        <div style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 320, lineHeight: 1.5, textWrap: 'pretty' }}>Add someone to start receiving notifications from this list.</div>
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
    <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', tableLayout: 'auto' }}>
      <thead>
        <tr><MTh>Email</MTh><MTh>Name</MTh><MTh>Added</MTh><MTh align="right" width={64}>{''}</MTh></tr>
      </thead>
      <tbody>
        {active.map((m) => {
          const busy = removingId === m.id;
          const added = fmtDate(m.created_at);
          return (
            <tr key={m.id} className="s-row" style={{ background: 'var(--bg-surface)', opacity: busy ? 0.55 : 1 }}>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }}>
                <span className="mono" style={{ fontSize: 14, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{m.email}</span>
              </td>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }}>
                {m.name ? <span style={{ fontSize: 14, color: 'var(--text-2)', whiteSpace: 'nowrap' }}>{m.name}</span> : <span style={{ fontSize: 13.5, color: 'var(--text-4)' }}>—</span>}
              </td>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }}>
                {added ? <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{added}</span> : <span style={{ fontSize: 13.5, color: 'var(--text-4)' }}>—</span>}
              </td>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right', verticalAlign: 'middle' }}>
                <button type="button" onClick={() => !busy && !removingId && onRemove(m)} disabled={busy || (removingId && removingId !== m.id)} aria-label={'Remove ' + m.email}
                  className="s-trash" style={{
                    width: 30, height: 30, borderRadius: 7, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    color: busy ? 'var(--text-4)' : 'var(--text-3)', background: 'transparent', border: '1px solid transparent',
                    cursor: busy ? 'default' : (removingId ? 'not-allowed' : 'pointer'), opacity: removingId && !busy ? 0.5 : 1,
                  }}>
                  {busy ? <StIcon name="spin" size={14} sw={2.2} /> : <StIcon name="trash" size={15} sw={1.7} />}
                </button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
};

/* ─────────────── enable/disable toggle ─────────────── */
const ToggleButton = ({ active, busy, onClick }) => {
  const tone = active ? 'var(--warning)' : 'var(--accent-text)';
  const ring = active ? 'color-mix(in oklch, var(--warning) 30%, var(--border-default))' : 'color-mix(in oklch, var(--accent) 30%, var(--border-default))';
  return (
    <button type="button" onClick={() => !busy && onClick()} disabled={busy} className={active ? 's-toggle-off' : 's-toggle-on'} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 12px', borderRadius: 8,
      fontSize: 14, fontWeight: 600, color: tone, whiteSpace: 'nowrap',
      background: 'var(--bg-surface)', border: '1px solid ' + ring, boxShadow: 'var(--shadow-xs)',
      cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1,
    }}>
      {busy ? <StIcon name="spin" size={13} sw={2.4} /> : <StIcon name="power" size={14} sw={1.9} />}
      {busy ? (active ? 'Disabling…' : 'Enabling…') : (active ? 'Disable' : 'Enable')}
    </button>
  );
};

/* ─────────────── members panel ─────────────── */
const MembersPanel = ({ list, members, loading, onAddMember, onRemoveMember, removingId, onToggle, toggling }) => (
  <div style={{ flex: 1, minWidth: 0, alignSelf: 'flex-start', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '16px 18px', borderBottom: '1px solid var(--border-subtle)' }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span className="mono" style={{ fontSize: 16.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>{list.list_name}</span>
          <StatusBadge active={!!list.is_active} size="sm" />
          <CountChip count={list.member_count} />
        </div>
        {list.description
          ? <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty', maxWidth: 560 }}>{list.description}</p>
          : <p style={{ margin: '6px 0 0', fontSize: 14, color: 'var(--text-4)', fontStyle: 'italic' }}>No description</p>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: '0 0 auto' }}>
        <ToggleButton active={!!list.is_active} busy={toggling} onClick={onToggle} />
        <button type="button" className="s-primary" onClick={onAddMember} style={primaryBtn}>
          <StIcon name="user-plus" size={14} sw={2} />Add Member
        </button>
      </div>
    </div>
    {loading
      ? <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '44px 24px' }}>
          <div style={{ width: 20, height: 20, borderRadius: 999, border: '2.5px solid var(--border-default)', borderTopColor: 'var(--accent)', animation: 'sSpin 0.7s linear infinite' }} />
          <span style={{ fontSize: 14.5, color: 'var(--text-3)' }}>Loading members…</span>
        </div>
      : <MemberTable members={members} onRemove={onRemoveMember} removingId={removingId} />}
  </div>
);

/* ─────────────── modal shell + form fields ─────────────── */
const ModalShell = ({ title, subtitle, onClose, busy, children, footer, width = 480 }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onClose]);
  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }} style={{
      position: 'fixed', inset: 0, zIndex: 10000, display: 'grid', placeItems: 'center',
      background: 'oklch(0.2 0.008 100 / 0.34)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', padding: 24,
    }}>
      <div role="dialog" aria-modal="true" style={{
        width, maxWidth: '100%', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 14,
        boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.22)', overflow: 'hidden',
        fontFamily: 'var(--font-sans)', color: 'var(--text-1)', letterSpacing: '-0.005em',
        display: 'flex', flexDirection: 'column', maxHeight: '92%', minHeight: 0,
      }}>
        <header style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '18px 20px 14px', borderBottom: '1px solid var(--border-subtle)' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: 17, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.012em' }}>{title}</h2>
            {subtitle && <p style={{ margin: '5px 0 0', fontSize: 14, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty' }}>{subtitle}</p>}
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="s-modal-x" style={{
            width: 26, height: 26, borderRadius: 6, color: 'var(--text-3)', flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent', border: '1px solid transparent', cursor: busy ? 'default' : 'pointer',
          }}><StIcon name="x" size={14} /></button>
        </header>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '18px 20px' }}>{children}</div>
        {footer && (
          <footer style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, padding: '12px 20px 16px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>{footer}</footer>
        )}
      </div>
    </div>
  );
};

const Label = ({ children, required, hint }) => (
  <label style={{ display: 'flex', alignItems: 'baseline', gap: 6, fontSize: 12.5, fontWeight: 600, color: 'var(--text-2)', letterSpacing: '0.01em', marginBottom: 6 }}>
    <span>{children}{required && <span style={{ color: 'var(--danger)', marginLeft: 3 }}>*</span>}</span>
    {hint && <span style={{ fontWeight: 500, color: 'var(--text-4)', letterSpacing: 0 }}>{hint}</span>}
  </label>
);

const fieldBase = (focused, mono = false) => ({
  width: '100%', height: 36, padding: '0 12px', borderRadius: 8, boxSizing: 'border-box',
  fontFamily: mono ? 'var(--font-mono)' : 'var(--font-sans)', fontSize: 14.5,
  color: 'var(--text-1)', background: 'var(--bg-surface)', outline: 'none',
  border: '1px solid ' + (focused ? 'var(--accent)' : 'var(--border-default)'),
  boxShadow: focused ? '0 0 0 3px var(--accent-ring)' : 'none',
  letterSpacing: mono ? '-0.02em' : '-0.005em', transition: 'border-color 120ms, box-shadow 120ms',
});

const TextInput = ({ value, onChange, placeholder, type = 'text', mono = false, autoFocus = false, multiline = false }) => {
  const [f, setF] = useState(false);
  if (multiline) {
    return (
      <textarea value={value} autoFocus={autoFocus} rows={3}
        onChange={(e) => onChange(e.target.value)} onFocus={() => setF(true)} onBlur={() => setF(false)}
        placeholder={placeholder} style={{ ...fieldBase(f, mono), height: 'auto', minHeight: 72, padding: '9px 12px', resize: 'vertical', lineHeight: 1.5 }} />
    );
  }
  return (
    <input value={value} type={type} autoFocus={autoFocus}
      onChange={(e) => onChange(e.target.value)} onFocus={() => setF(true)} onBlur={() => setF(false)}
      placeholder={placeholder} style={fieldBase(f, mono)} />
  );
};

const ghostBtn = (busy) => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 13px', borderRadius: 8,
  fontSize: 14, fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap',
  background: 'var(--bg-surface)', border: '1px solid var(--border-default)', boxShadow: 'var(--shadow-xs)',
  cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1,
});

const SubmitBtn = ({ children, icon, busy, onClick }) => (
  <button type="button" onClick={() => !busy && onClick()} disabled={busy} style={{ ...primaryBtn, padding: '0 15px', opacity: busy ? 0.85 : 1, cursor: busy ? 'default' : 'pointer' }}>
    {busy ? <StIcon name="spin" size={13} sw={2.4} /> : <StIcon name={icon} size={13} sw={2.2} />}
    {children}
  </button>
);

const ErrorBanner = ({ children }) => (
  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 12px', borderRadius: 8, background: 'var(--danger-soft)', border: '1px solid color-mix(in oklch, var(--danger) 22%, transparent)' }}>
    <span style={{ color: 'var(--danger)', flex: '0 0 auto', display: 'inline-flex', marginTop: 1 }}><StIcon name="alert-tri" size={15} sw={1.8} /></span>
    <span style={{ fontSize: 14, color: 'var(--danger)', fontWeight: 500, lineHeight: 1.45, textWrap: 'pretty' }}>{children}</span>
  </div>
);

/* ─────────────── New List modal ─────────────── */
const NewListModal = ({ existingNames, onClose, onSubmit }) => {
  const [form, setForm] = useState({ list_name: '', description: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (busy) return;
    const name = form.list_name.trim();
    if (!name) return setError('List name is required.');
    if (!/^[a-z0-9][a-z0-9-]*$/.test(name)) return setError('Use lowercase letters, numbers, and hyphens — e.g. “daily-digest”.');
    if ((existingNames || []).map((n) => n.toLowerCase()).includes(name.toLowerCase())) return setError('A list with that name already exists.');
    setError(null);
    setBusy(true);
    try {
      await onSubmit({ list_name: name, description: form.description.trim() || null });
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="New Notification List" width={480} onClose={onClose} busy={busy}
      subtitle="Create a recipient list that automated alerts can target. Members are added after the list exists."
      footer={<>
        <button type="button" style={ghostBtn(busy)} onClick={() => !busy && onClose()}>Cancel</button>
        <SubmitBtn icon="check" busy={busy} onClick={submit}>{busy ? 'Creating…' : 'Create List'}</SubmitBtn>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <div>
          <Label required hint="· lowercase, hyphenated">List Name</Label>
          <TextInput value={form.list_name} onChange={set('list_name')} placeholder="e.g. daily-digest" mono autoFocus />
        </div>
        <div>
          <Label hint="· optional">Description</Label>
          <TextInput value={form.description} onChange={set('description')} multiline placeholder="What is this list for? Who should be on it?" />
        </div>
      </div>
    </ModalShell>
  );
};

/* ─────────────── Add Member modal ─────────────── */
const AddMemberModal = ({ listName, existingEmails, onClose, onSubmit }) => {
  const [form, setForm] = useState({ email: '', name: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (busy) return;
    const email = form.email.trim();
    if (!email) return setError('Email is required.');
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError('Enter a valid email address.');
    if ((existingEmails || []).map((e) => e.toLowerCase()).includes(email.toLowerCase())) return setError('That email is already on this list.');
    setError(null);
    setBusy(true);
    try {
      await onSubmit({ email, name: form.name.trim() || null });
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add Member" width={460} onClose={onClose} busy={busy}
      subtitle={<>Add a recipient to <strong className="mono" style={{ color: 'var(--text-2)', fontWeight: 600, letterSpacing: '-0.01em' }}>{listName}</strong>. They’ll receive every alert this list sends.</>}
      footer={<>
        <button type="button" style={ghostBtn(busy)} onClick={() => !busy && onClose()}>Cancel</button>
        <SubmitBtn icon="user-plus" busy={busy} onClick={submit}>{busy ? 'Adding…' : 'Add'}</SubmitBtn>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <div>
          <Label required>Email</Label>
          <TextInput value={form.email} onChange={set('email')} placeholder="name@e3summit.com" type="email" mono autoFocus />
        </div>
        <div>
          <Label hint="· optional">Name</Label>
          <TextInput value={form.name} onChange={set('name')} placeholder="e.g. Alex Morgan" />
        </div>
      </div>
    </ModalShell>
  );
};

/* ─────────────── confirm dialog (remove member / generic) ───────────────
   Defaults to a destructive (danger/trash/"Remove") confirm — the original
   member-removal use. Pass confirmKind="primary" + icon/confirmLabel/busyLabel
   for a non-destructive action (e.g. "Pull job cost now"). */
const ConfirmDialog = ({ title, body, busy, onCancel, onConfirm, confirmKind = 'danger', icon, confirmLabel, busyLabel }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [busy, onCancel]);
  const isPrimary = confirmKind === 'primary';
  const headIcon = icon || (isPrimary ? 'refresh' : 'trash');
  const cLabel = confirmLabel || (isPrimary ? 'Confirm' : 'Remove');
  const bLabel = busyLabel || (isPrimary ? 'Working…' : 'Removing…');
  const headTone = isPrimary ? 'var(--accent)' : 'var(--danger)';
  const headSoft = isPrimary ? 'var(--accent-soft)' : 'var(--danger-soft)';
  const confirmBtnStyle = isPrimary
    ? { ...primaryBtn, height: 34, padding: '0 15px', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.85 : 1 }
    : {
        display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 15px', fontSize: 14, fontWeight: 600,
        color: 'oklch(0.99 0.005 25)', background: 'var(--danger)', border: '1px solid color-mix(in oklch, var(--danger) 80%, oklch(0.2 0.05 25))', borderRadius: 8,
        boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.18), 0 1px 0 oklch(0.35 0.1 25)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.85 : 1,
      };
  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel(); }} style={{
      position: 'fixed', inset: 0, zIndex: 10001, display: 'grid', placeItems: 'center',
      background: 'oklch(0.2 0.008 100 / 0.34)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', padding: 24,
    }}>
      <div role="dialog" aria-modal="true" style={{
        width: 420, maxWidth: '100%', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 14,
        boxShadow: 'var(--shadow-md), 0 24px 60px -12px oklch(0.2 0.01 100 / 0.22)', padding: '22px 22px 18px',
        fontFamily: 'var(--font-sans)', color: 'var(--text-1)', letterSpacing: '-0.005em',
      }}>
        <div style={{ width: 36, height: 36, borderRadius: 9, background: headSoft, border: '1px solid color-mix(in oklch, ' + headTone + ' 22%, transparent)', display: 'grid', placeItems: 'center', color: headTone, marginBottom: 16 }}>
          <StIcon name={headIcon} size={17} sw={1.8} />
        </div>
        <h2 style={{ margin: 0, fontSize: 17.5, fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.3 }}>{title}</h2>
        <p style={{ margin: '6px 0 0', fontSize: 14.5, lineHeight: 1.5, color: 'var(--text-2)', textWrap: 'pretty' }}>{body}</p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
          <button type="button" onClick={() => !busy && onCancel()} style={{ height: 34, padding: '0 14px', fontSize: 14, fontWeight: 500, color: 'var(--text-1)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 8, boxShadow: 'var(--shadow-xs)', cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.6 : 1 }}>Cancel</button>
          <button type="button" onClick={() => !busy && onConfirm()} style={confirmBtnStyle}>
            {busy ? <StIcon name="spin" size={13} sw={2.4} /> : <StIcon name={headIcon} size={13} sw={2} />}
            {busy ? bLabel : cLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── page banner ─────────────── */
const Banner = ({ kind, children, onDismiss }) => {
  const danger = kind === 'error';
  const tone = danger ? 'var(--danger)' : 'var(--success)';
  const soft = danger ? 'var(--danger-soft)' : 'var(--success-soft)';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, background: soft, border: '1px solid color-mix(in oklch, ' + tone + ' 22%, transparent)' }}>
      <span style={{
        width: 22, height: 22, borderRadius: 6, flex: '0 0 auto', color: tone,
        background: 'color-mix(in oklch, ' + tone + ' 16%, var(--bg-surface))', border: '1px solid color-mix(in oklch, ' + tone + ' 22%, transparent)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      }}><StIcon name={danger ? 'alert-tri' : 'check'} size={13} sw={2.2} /></span>
      <span style={{ flex: 1, fontSize: 14, color: tone, fontWeight: 600, letterSpacing: '-0.005em' }}>{children}</span>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="s-banner-x" style={{ width: 22, height: 22, borderRadius: 6, flex: '0 0 auto', color: tone, background: 'transparent', border: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
        <StIcon name="x" size={13} sw={2} />
      </button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Job Cost Roster section
   A roster of CE job numbers the team pulls job cost for (existing jobs not
   ported into Summit billing). Summit drives the CE-Agent's inception-to-date
   pull — manually ("Pull job cost now") or on a cadence (Off/Daily/Weekly).
   ═══════════════════════════════════════════════════════════════════════════ */

/* date+time formatter for "Last pulled" (created/last_pulled_at are UTC). */
const fmtDateTime = (v) => {
  if (v == null || v === '') return null;
  const s = typeof v === 'string' ? (/(Z|[+-]\d\d:?\d\d)$/.test(v) ? v : v + 'Z') : v;
  const x = s instanceof Date ? s : new Date(s);
  if (isNaN(x)) return null;
  return x.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/* pull-status badge: queued/blue · success/green · partial/amber · failed/red */
const PULL_STATUS_TONE = {
  queued: 'var(--accent)',
  success: 'var(--success)',
  partial: 'var(--warning)',
  failed: 'var(--danger)',
};
const PullStatusBadge = ({ status }) => {
  if (!status) return <span style={{ fontSize: 13.5, color: 'var(--text-4)' }}>—</span>;
  const tone = PULL_STATUS_TONE[status] || 'var(--text-4)';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, height: 22, padding: '0 9px 0 8px', borderRadius: 6,
      background: 'color-mix(in oklch, ' + tone + ' 14%, var(--bg-surface))',
      border: '1px solid color-mix(in oklch, ' + tone + ' 24%, transparent)',
      fontSize: 11.5, fontWeight: 600, color: tone, whiteSpace: 'nowrap', textTransform: 'capitalize',
    }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, flex: '0 0 auto', background: tone }} />
      {status}
    </span>
  );
};

/* enabled pill (compact inline toggle inside the table) */
const EnabledPill = ({ enabled, busy, onClick }) => {
  const tone = enabled ? 'var(--success)' : 'var(--text-4)';
  return (
    <button type="button" onClick={() => !busy && onClick()} disabled={busy} className="s-enabled-pill" style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, height: 24, padding: '0 9px 0 8px', borderRadius: 999,
      background: 'color-mix(in oklch, ' + tone + ' 12%, var(--bg-surface))',
      border: '1px solid color-mix(in oklch, ' + tone + ' 24%, transparent)',
      fontSize: 12, fontWeight: 600, color: tone, whiteSpace: 'nowrap',
      cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.7 : 1,
    }}>
      {busy ? <StIcon name="spin" size={11} sw={2.4} /> : <span style={{ width: 6, height: 6, borderRadius: 999, flex: '0 0 auto', background: tone }} />}
      {enabled ? 'Enabled' : 'Disabled'}
    </button>
  );
};

/* cadence selector (Off / Daily / Weekly) */
const CADENCE_OPTIONS = [
  { value: 'off', label: 'Off' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
];
const CadenceSelector = ({ value, busy, onChange }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: 3, borderRadius: 9, background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)' }}>
    {CADENCE_OPTIONS.map((opt) => {
      const on = opt.value === value;
      return (
        <button key={opt.value} type="button" onClick={() => !busy && !on && onChange(opt.value)} disabled={busy} style={{
          height: 28, padding: '0 12px', borderRadius: 7, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap',
          color: on ? 'var(--accent-text)' : 'var(--text-3)',
          background: on ? 'var(--bg-surface)' : 'transparent',
          border: '1px solid ' + (on ? 'color-mix(in oklch, var(--accent) 24%, transparent)' : 'transparent'),
          boxShadow: on ? 'var(--shadow-xs)' : 'none',
          cursor: busy || on ? 'default' : 'pointer', opacity: busy ? 0.7 : 1,
        }}>{opt.label}</button>
      );
    })}
  </div>
);

/* CE job-number format: customer abbr (2-8) - project-type letter(s) - phase digits,
   e.g. BRYAN-D-03. Locks the roster to job NUMBERS so a job NAME (which has spaces)
   can't be added by mistake. If a real job legitimately doesn't fit, loosen here. */
const JOB_NUMBER_RE = /^[A-Z0-9]{2,8}-[A-Z]{1,2}-\d{1,3}$/;

/* Add-job-to-roster modal */
const AddRosterJobModal = ({ existingNumbers, onClose, onSubmit }) => {
  const [form, setForm] = useState({ job_number: '', notes: '', enabled: true });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));
  // Normalize as you type: job numbers are upper-case and have no spaces, so the
  // field can never hold a pasted job name.
  const setJobNumber = (v) => set('job_number')(String(v).toUpperCase().replace(/\s+/g, ''));

  const submit = async () => {
    if (busy) return;
    const num = form.job_number.trim().toUpperCase();
    if (!num) return setError('Job number is required.');
    if (!JOB_NUMBER_RE.test(num)) {
      return setError('Enter a CE job NUMBER like BRYAN-D-03 (customer-type-phase) — not a job name.');
    }
    if ((existingNumbers || []).map((n) => n.toUpperCase()).includes(num)) {
      return setError('That job number is already on the roster.');
    }
    setError(null);
    setBusy(true);
    try {
      await onSubmit({ job_number: num, notes: form.notes.trim() || null, enabled: form.enabled });
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(false);
    }
  };

  return (
    <ModalShell title="Add Job to Roster" width={460} onClose={onClose} busy={busy}
      subtitle="Add a CE job number to pull job cost for. The pull is inception-to-date — Summit drives the CE-Agent to run the report."
      footer={<>
        <button type="button" style={ghostBtn(busy)} onClick={() => !busy && onClose()}>Cancel</button>
        <SubmitBtn icon="check" busy={busy} onClick={submit}>{busy ? 'Adding…' : 'Add Job'}</SubmitBtn>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <div>
          <Label required hint="· format like BRYAN-D-03">Job Number</Label>
          <TextInput value={form.job_number} onChange={setJobNumber} placeholder="e.g. BRYAN-D-03" mono autoFocus />
        </div>
        <div>
          <Label hint="· optional">Notes</Label>
          <TextInput value={form.notes} onChange={set('notes')} multiline placeholder="What is this job? Why are we tracking its cost?" />
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 9, cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled')(e.target.checked)} style={{ width: 16, height: 16, accentColor: 'var(--accent)', cursor: 'pointer' }} />
          <span style={{ fontSize: 14, color: 'var(--text-2)', fontWeight: 500 }}>Enabled <span style={{ color: 'var(--text-4)', fontWeight: 400 }}>· included in pulls</span></span>
        </label>
      </div>
    </ModalShell>
  );
};

/* Archive-job modal — file a roster job under a year (data kept) or, via a
   secondary danger action in the same dialog, permanently delete + purge its
   pulled cost data. Reuses the ModalShell / field styling from this file. */
const ArchiveJobModal = ({ job, onClose, onArchive, onDelete }) => {
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [busy, setBusy] = useState(null); // null | 'archive' | 'delete'
  const [error, setError] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [yf, setYf] = useState(false);
  const busyAny = busy != null;
  // current year+1 down to current year-6
  const years = [];
  for (let y = thisYear + 1; y >= thisYear - 6; y--) years.push(y);

  const doArchive = async () => {
    if (busyAny) return;
    setError(null);
    setBusy('archive');
    try {
      await onArchive(year);
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(null);
    }
  };

  const doDelete = async () => {
    if (busyAny) return;
    setError(null);
    setBusy('delete');
    try {
      await onDelete();
    } catch (e) {
      setError(e.message || 'Something went wrong. Please try again.');
      setBusy(null);
      setConfirmDelete(false);
    }
  };

  return (
    <ModalShell title="Archive this job" width={460} onClose={onClose} busy={busyAny}
      subtitle={<>File <strong className="mono" style={{ color: 'var(--text-2)', fontWeight: 600, letterSpacing: '-0.01em' }}>{job.job_number}</strong> under a year. It stops being pulled and drops off the active list — its pulled cost data is kept, and you can restore it anytime from the Job Costs page.</>}
      footer={<>
        <button type="button" style={ghostBtn(busyAny)} onClick={() => !busyAny && onClose()}>Cancel</button>
        <SubmitBtn icon="archive" busy={busy === 'archive'} onClick={doArchive}>{busy === 'archive' ? 'Archiving…' : `Archive to ${year}`}</SubmitBtn>
      </>}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && <ErrorBanner>{error}</ErrorBanner>}
        <div>
          <Label required hint="· file under">Year</Label>
          <div style={{ position: 'relative', display: 'flex' }}>
            <select
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
              onFocus={() => setYf(true)} onBlur={() => setYf(false)}
              disabled={busyAny}
              style={{
                ...fieldBase(yf), appearance: 'none', WebkitAppearance: 'none', padding: '0 32px 0 12px',
                fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.01em',
                cursor: busyAny ? 'default' : 'pointer', opacity: busyAny ? 0.6 : 1,
              }}>
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
            <span style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)', pointerEvents: 'none', display: 'inline-flex' }}>
              <StIcon name="chevron-d" size={14} />
            </span>
          </div>
        </div>

        {/* Secondary danger action — permanently delete + purge */}
        <div style={{ marginTop: 4, padding: '13px 14px', borderRadius: 10, background: 'var(--danger-soft)', border: '1px solid color-mix(in oklch, var(--danger) 18%, transparent)' }}>
          <div style={{ fontSize: 12.5, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--danger)', marginBottom: 4 }}>Danger zone</div>
          <p style={{ margin: '0 0 10px', fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.45, textWrap: 'pretty' }}>
            This erases the pulled cost data — it can't be undone. Prefer Archive if you might need this job's numbers again.
          </p>
          {confirmDelete ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={doDelete} disabled={busyAny} style={{
                display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 13px', fontSize: 13.5, fontWeight: 600,
                color: 'oklch(0.99 0.005 25)', background: 'var(--danger)', border: '1px solid color-mix(in oklch, var(--danger) 80%, oklch(0.2 0.05 25))', borderRadius: 8,
                boxShadow: 'inset 0 1px 0 oklch(1 0 0 / 0.18), 0 1px 0 oklch(0.35 0.1 25)', cursor: busyAny ? 'default' : 'pointer', opacity: busyAny ? 0.85 : 1, whiteSpace: 'nowrap',
              }}>
                {busy === 'delete' ? <StIcon name="spin" size={13} sw={2.4} /> : <StIcon name="trash" size={13} sw={2} />}
                {busy === 'delete' ? 'Deleting…' : 'Yes, delete permanently'}
              </button>
              <button type="button" onClick={() => !busyAny && setConfirmDelete(false)} disabled={busyAny} style={{ ...ghostBtn(busyAny), height: 32 }}>Keep it</button>
            </div>
          ) : (
            <button type="button" onClick={() => !busyAny && setConfirmDelete(true)} disabled={busyAny} className="s-danger-ghost" style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 13px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap',
              color: 'var(--danger)', background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--danger) 32%, var(--border-default))',
              boxShadow: 'var(--shadow-xs)', cursor: busyAny ? 'default' : 'pointer', opacity: busyAny ? 0.6 : 1,
            }}>
              <StIcon name="trash" size={13} sw={1.9} />Delete permanently (purge cost data)
            </button>
          )}
        </div>
      </div>
    </ModalShell>
  );
};

/* auto (from a CE-integrated project) vs manual (hand-added) chip */
const SourceChip = ({ manual }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 4, height: 17, padding: '0 7px', borderRadius: 999,
    fontSize: 10.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', flex: '0 0 auto',
    color: manual ? 'var(--text-3)' : 'var(--accent-text)',
    background: manual ? 'var(--bg-surface-2)' : 'var(--accent-soft)',
    border: '1px solid ' + (manual ? 'var(--border-subtle)' : 'color-mix(in oklch, var(--accent) 22%, transparent)'),
  }}>{manual ? 'Manual' : 'Auto'}</span>
);

/* roster table */
const RosterTable = ({ jobs, onToggle, onRemove, onPull, togglingId, removingId, pullingId }) => {
  if ((jobs || []).length === 0) {
    return (
      <div style={{ padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
        <span style={{ width: 44, height: 44, borderRadius: 11, color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <StIcon name="briefcase" size={20} sw={1.6} />
        </span>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>No jobs on the roster yet</div>
        <div style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 340, lineHeight: 1.5, textWrap: 'pretty' }}>Add a CE job number to start pulling its job cost — manually or on a schedule.</div>
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
    <table style={{ width: '100%', minWidth: 680, borderCollapse: 'collapse', tableLayout: 'auto' }}>
      <thead>
        <tr><MTh>Job #</MTh><MTh>Enabled</MTh><MTh>Last Pulled</MTh><MTh>Status</MTh><MTh align="right" width={92}>{''}</MTh></tr>
      </thead>
      <tbody>
        {jobs.map((j) => {
          const busyRow = removingId === j.id;
          const pullingRow = pullingId === j.id;
          const pulled = fmtDateTime(j.last_pulled_at);
          return (
            <tr key={j.id} className="s-row" style={{ background: 'var(--bg-surface)', opacity: busyRow ? 0.55 : 1 }}>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="mono" style={{ fontSize: 14, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap', fontWeight: 600 }}>{j.job_number}</span>
                    <SourceChip manual={j.source !== 'auto'} />
                  </div>
                  {(j.project_name || j.notes) ? <span style={{ fontSize: 12.5, color: 'var(--text-4)', maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.project_name || j.notes}</span> : null}
                </div>
              </td>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }}>
                <EnabledPill enabled={!!j.enabled} busy={togglingId === j.id} onClick={() => onToggle(j)} />
              </td>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }}>
                {pulled ? <span className="mono" style={{ fontSize: 13, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{pulled}</span> : <span style={{ fontSize: 13.5, color: 'var(--text-4)' }}>Never</span>}
              </td>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }}>
                <PullStatusBadge status={j.last_pull_status} />
              </td>
              <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right', verticalAlign: 'middle' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, justifyContent: 'flex-end' }}>
                  <button type="button" onClick={() => !pullingRow && !busyRow && onPull(j)} disabled={pullingRow || busyRow} aria-label={'Pull job cost for ' + j.job_number} title="Pull job cost for this job now"
                    className="s-rowpull" style={{
                      width: 30, height: 30, borderRadius: 7, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      color: pullingRow ? 'var(--accent-text)' : 'var(--text-3)', background: 'transparent', border: '1px solid transparent',
                      cursor: (pullingRow || busyRow) ? 'default' : 'pointer', opacity: busyRow ? 0.5 : 1,
                    }}>
                    {pullingRow ? <StIcon name="spin" size={14} sw={2.2} /> : <StIcon name="refresh" size={15} sw={1.7} />}
                  </button>
                  <button type="button" onClick={() => !busyRow && !removingId && onRemove(j)} disabled={busyRow || pullingRow || (removingId && removingId !== j.id)} aria-label={'Archive ' + j.job_number} title="Archive job (file under a year; keeps data) or delete permanently"
                    className="s-archive" style={{
                      width: 30, height: 30, borderRadius: 7, flex: '0 0 auto', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      color: busyRow ? 'var(--text-4)' : 'var(--text-3)', background: 'transparent', border: '1px solid transparent',
                      cursor: busyRow ? 'default' : (removingId ? 'not-allowed' : 'pointer'), opacity: removingId && !busyRow ? 0.5 : 1,
                    }}>
                    {busyRow ? <StIcon name="spin" size={14} sw={2.2} /> : <StIcon name="archive" size={15} sw={1.7} />}
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
};

/* Roster / Backfill tab toggle */
const JC_TABS = [{ value: 'roster', label: 'Roster' }, { value: 'backfill', label: 'Backfill' }];
const JcTabs = ({ value, onChange }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: 3, padding: 3, borderRadius: 9, background: 'var(--bg-surface-2)', border: '1px solid var(--border-default)', alignSelf: 'flex-start' }}>
    {JC_TABS.map((t) => {
      const on = t.value === value;
      return (
        <button key={t.value} type="button" onClick={() => !on && onChange(t.value)} style={{
          height: 30, padding: '0 16px', borderRadius: 7, fontSize: 13.5, fontWeight: 600,
          color: on ? 'var(--accent-text)' : 'var(--text-3)',
          background: on ? 'var(--bg-surface)' : 'transparent',
          border: '1px solid ' + (on ? 'color-mix(in oklch, var(--accent) 24%, transparent)' : 'transparent'),
          boxShadow: on ? 'var(--shadow-xs)' : 'none', cursor: on ? 'default' : 'pointer',
        }}>{t.label}</button>
      );
    })}
  </div>
);

const jcTd = { padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' };

/* start-month field — holds its own local text state so typing is smooth (no
   parent re-render / refetch per keystroke); commits a valid 'YYYY-MM' only on
   blur or Enter, and reverts anything malformed. */
const StartMonthInput = ({ value, onCommit }) => {
  const [text, setText] = useState(value || '');
  useEffect(() => { setText(value || ''); }, [value]);
  const commit = () => {
    const v = text.trim();
    if (v === (value || '')) return;
    const y = parseInt(v.slice(0, 4), 10);
    const ok = /^\d{4}-\d{2}$/.test(v) && y >= 2000 && y <= new Date().getFullYear() + 1;
    if (ok) onCommit(v);
    else setText(value || ''); // revert anything malformed or an implausible year
  };
  return (
    <input
      type="text" inputMode="numeric" placeholder="YYYY-MM" maxLength={7}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      style={{
        width: 104, height: 32, padding: '0 10px', borderRadius: 8, border: '1px solid var(--border-default)',
        background: 'var(--bg-surface)', color: 'var(--text-1)', fontFamily: 'var(--font-mono)',
        fontSize: 13, fontVariantNumeric: 'tabular-nums', outline: 'none',
      }}
    />
  );
};

/* Archived jobs — collapsible section so retired jobs stay visible + manageable
   (restore / delete) even with no cost data. Driven by the roster, not the data. */
const ArchivedRosterSection = ({ jobs, open, onToggle, onRestore, onDelete, busyId }) => {
  if (!jobs || jobs.length === 0) return null;
  return (
    <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden', marginTop: 16 }}>
      <button type="button" onClick={onToggle} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 8, padding: '13px 16px', background: 'transparent', border: 'none', borderBottom: open ? '1px solid var(--border-subtle)' : 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ color: 'var(--text-4)', display: 'inline-flex', transform: open ? 'none' : 'rotate(-90deg)', transition: 'transform .15s' }}><StIcon name="chevron-d" size={14} sw={2} /></span>
        <span style={{ color: 'var(--text-4)', display: 'inline-flex' }}><StIcon name="archive" size={14} /></span>
        <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-4)' }}>Archived</span>
        <span className="mono" style={{ minWidth: 18, height: 18, padding: '0 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>{jobs.length}</span>
      </button>
      {open && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {jobs.map((j) => {
              const busy = busyId === j.id;
              return (
                <tr key={j.id} style={{ background: 'var(--bg-surface)', opacity: busy ? 0.55 : 1 }}>
                  <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'middle' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span className="mono" style={{ fontSize: 14, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap', fontWeight: 600 }}>{j.job_number}</span>
                      {j.archive_year != null && <span className="mono" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', borderRadius: 6, padding: '1px 6px' }}>{j.archive_year}</span>}
                    </div>
                    {j.project_name ? <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}>{j.project_name}</span> : null}
                  </td>
                  <td style={{ padding: '11px 16px', borderBottom: '1px solid var(--border-subtle)', textAlign: 'right', verticalAlign: 'middle', whiteSpace: 'nowrap' }}>
                    <button type="button" onClick={() => !busyId && onRestore(j)} disabled={!!busyId}
                      style={{ height: 30, padding: '0 12px', borderRadius: 7, fontSize: 13, fontWeight: 600, color: 'var(--accent-text)', background: 'var(--accent-soft)', border: '1px solid color-mix(in oklch, var(--accent) 22%, transparent)', cursor: busyId ? 'default' : 'pointer', marginRight: 8, verticalAlign: 'middle' }}>
                      {busy ? 'Working…' : 'Restore'}
                    </button>
                    <button type="button" onClick={() => !busyId && onDelete(j)} disabled={!!busyId} aria-label={'Delete ' + j.job_number} title="Delete permanently (purge cost data)"
                      className="s-trash" style={{ width: 30, height: 30, borderRadius: 7, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', background: 'transparent', border: '1px solid transparent', cursor: busyId ? 'default' : 'pointer', verticalAlign: 'middle' }}>
                      <StIcon name="trash" size={15} sw={1.7} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
};

/* backfill table — per-job historical import status + action */
const BackfillTable = ({ jobs, onSetStart, onBackfill, busyNumber }) => {
  if ((jobs || []).length === 0) {
    return (
      <div style={{ padding: '40px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
        <span style={{ width: 44, height: 44, borderRadius: 11, color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <StIcon name="briefcase" size={20} sw={1.6} />
        </span>
        <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>No jobs to backfill</div>
        <div style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 360, lineHeight: 1.5, textWrap: 'pretty' }}>Projects with ComputerEase integration appear here. Set each one's start month, then backfill its cost history month by month.</div>
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
    <table style={{ width: '100%', minWidth: 720, borderCollapse: 'collapse', tableLayout: 'auto' }}>
      <thead>
        <tr><MTh>Job #</MTh><MTh>Start Month</MTh><MTh>History filled</MTh><MTh align="right" width={150}>{''}</MTh></tr>
      </thead>
      <tbody>
        {jobs.map((j) => {
          const busy = busyNumber === j.job_number;
          const total = j.months_total || 0;
          const done = j.months_done || 0;
          const missing = j.missing_count || 0;
          const hasStart = !!j.start_month;
          const complete = hasStart && total > 0 && missing === 0;
          const pctW = total > 0 ? Math.round((done / total) * 100) : 0;
          return (
            <tr key={j.id} className="s-row" style={{ background: 'var(--bg-surface)' }}>
              <td style={jcTd}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span className="mono" style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>{j.job_number}</span>
                    <SourceChip manual={j.source !== 'auto'} />
                  </div>
                  {j.project_name ? <span style={{ fontSize: 12.5, color: 'var(--text-4)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{j.project_name}</span> : null}
                </div>
              </td>
              <td style={jcTd}>
                <StartMonthInput value={j.start_month} onCommit={(m) => onSetStart(j, m)} />
              </td>
              <td style={jcTd}>
                {!hasStart ? (
                  <span style={{ fontSize: 13, color: 'var(--text-4)' }}>Set a start month →</span>
                ) : total === 0 ? (
                  <span style={{ fontSize: 13, color: 'var(--text-4)' }}>Up to date</span>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 130, height: 6, borderRadius: 999, background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${pctW}%`, background: complete ? 'var(--success)' : 'var(--accent)' }} />
                    </div>
                    <span className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{done} / {total} mo</span>
                  </div>
                )}
              </td>
              <td style={{ ...jcTd, textAlign: 'right' }}>
                {complete ? (
                  <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--success)', whiteSpace: 'nowrap' }}>✓ Complete</span>
                ) : (
                  <button type="button" onClick={() => hasStart && missing > 0 && !busy && onBackfill(j)} disabled={!hasStart || missing === 0 || busy} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, height: 32, padding: '0 13px', borderRadius: 8, fontSize: 13.5, fontWeight: 600, whiteSpace: 'nowrap',
                    color: 'var(--accent-text)', background: 'var(--bg-surface)',
                    border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border-default))', boxShadow: 'var(--shadow-xs)',
                    cursor: (!hasStart || missing === 0 || busy) ? 'default' : 'pointer', opacity: (!hasStart || missing === 0 || busy) ? 0.5 : 1,
                  }}>
                    {busy ? <><StIcon name="spin" size={13} sw={2.2} /> Queuing…</> : `Backfill ${missing} mo`}
                  </button>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
    </div>
  );
};

const JobCostRosterSection = () => {
  const [jobs, setJobs] = useState([]);
  const [cadence, setCadence] = useState('off');
  const [loading, setLoading] = useState(true);
  const [banner, setBanner] = useState(null); // null | { kind, text }
  const [modalOpen, setModalOpen] = useState(false);
  const [confirmJob, setConfirmJob] = useState(null);
  const [confirmPull, setConfirmPull] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [savingCadence, setSavingCadence] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullingId, setPullingId] = useState(null); // roster row id being pulled solo
  const [tab, setTab] = useState('roster');
  const [bfJobs, setBfJobs] = useState([]);
  const [bfLoading, setBfLoading] = useState(false);
  const [bfBusy, setBfBusy] = useState(null); // job_number currently queuing
  const [archivedJobs, setArchivedJobs] = useState([]);
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archBusyId, setArchBusyId] = useState(null); // archived row currently restoring/deleting
  const [confirmArchDelete, setConfirmArchDelete] = useState(null);

  const fetchRoster = useCallback(async () => {
    const data = await getJobCostRoster();
    setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    setArchivedJobs(Array.isArray(data.archived) ? data.archived : []);
    if (data.cadence) setCadence(data.cadence);
    return data;
  }, []);

  useEffect(() => {
    (async () => {
      try { await fetchRoster(); }
      catch (e) { setBanner({ kind: 'error', text: e.message }); }
      finally { setLoading(false); }
    })();
  }, [fetchRoster]);

  const handleAddJob = async (data) => {
    await saveJobCostRosterEntry(data);
    await fetchRoster();
    setModalOpen(false);
    setBanner({ kind: 'success', text: `Added ${data.job_number} to the roster.` });
  };

  const handleToggle = async (job) => {
    if (togglingId) return;
    try {
      setTogglingId(job.id);
      // Upsert by job_number flips enabled; notes preserved.
      await saveJobCostRosterEntry({ job_number: job.job_number, enabled: !job.enabled, notes: job.notes });
      await fetchRoster();
    } catch (e) {
      setBanner({ kind: 'error', text: e.message });
    } finally {
      setTogglingId(null);
    }
  };

  const handleArchive = async (year) => {
    if (!confirmJob || removingId) return;
    const job = confirmJob;
    setRemovingId(job.id);
    try {
      await archiveJobCostRosterEntry(job.id, year);
      await fetchRoster();
      setBanner({ kind: 'success', text: `Archived ${job.job_number} to ${year}.` });
      setConfirmJob(null);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || `Failed to archive ${job.job_number}.` });
    } finally {
      setRemovingId(null);
    }
  };

  const handleDelete = async () => {
    if (!confirmJob || removingId) return;
    const job = confirmJob;
    setRemovingId(job.id);
    try {
      const res = await purgeJobCostRosterEntry(job.id);
      await fetchRoster();
      if (res && res.purge_error) {
        setBanner({ kind: 'error', text: `Deleted ${job.job_number}, but the cost-data purge hit an error: ${res.purge_error}` });
      } else if (res && res.data_purged === false) {
        setBanner({ kind: 'success', text: `Deleted ${job.job_number}. Its cost data purge must be finished on the VM.` });
      } else {
        const rows = res && typeof res.purged_rows === 'number' ? res.purged_rows : null;
        setBanner({ kind: 'success', text: `Deleted ${job.job_number}${rows != null ? ` (purged ${rows} cost row${rows === 1 ? '' : 's'})` : ''}.` });
      }
      setConfirmJob(null);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || `Failed to delete ${job.job_number}.` });
    } finally {
      setRemovingId(null);
    }
  };

  const handleRestoreArchived = async (job) => {
    if (archBusyId) return;
    setArchBusyId(job.id);
    try {
      await restoreJobCostRosterEntry(job.id);
      await fetchRoster();
      setBanner({ kind: 'success', text: `Restored ${job.job_number} to active.` });
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || `Failed to restore ${job.job_number}.` });
    } finally {
      setArchBusyId(null);
    }
  };

  const handleDeleteArchived = async () => {
    const job = confirmArchDelete;
    if (!job || archBusyId) return;
    setArchBusyId(job.id);
    try {
      const res = await purgeJobCostRosterEntry(job.id);
      await fetchRoster();
      if (res && res.purge_error) {
        setBanner({ kind: 'error', text: `Deleted ${job.job_number}, but the cost-data purge hit an error: ${res.purge_error}` });
      } else if (res && res.data_purged === false) {
        setBanner({ kind: 'success', text: `Deleted ${job.job_number}. Its cost-data purge must be finished on the VM.` });
      } else {
        const rows = res && typeof res.purged_rows === 'number' ? res.purged_rows : null;
        setBanner({ kind: 'success', text: `Deleted ${job.job_number}${rows != null ? ` (purged ${rows} cost row${rows === 1 ? '' : 's'})` : ''}.` });
      }
      setConfirmArchDelete(null);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || `Failed to delete ${job.job_number}.` });
    } finally {
      setArchBusyId(null);
    }
  };

  const handleCadence = async (next) => {
    if (savingCadence) return;
    const prev = cadence;
    setCadence(next); // optimistic
    try {
      setSavingCadence(true);
      await setJobCostRosterCadence(next);
      setBanner({ kind: 'success', text: next === 'off' ? 'Scheduled pulls turned off.' : `Scheduled pulls set to ${next}.` });
    } catch (e) {
      setCadence(prev); // revert
      setBanner({ kind: 'error', text: e.message });
    } finally {
      setSavingCadence(false);
    }
  };

  const enabledCount = jobs.filter((j) => j.enabled).length;

  const handlePullAll = async () => {
    if (pulling) return;
    setConfirmPull(false);
    try {
      setPulling(true);
      setBanner(null);
      const result = await pullJobCostRoster();
      await fetchRoster();
      const queued = result.queued || 0;
      const errCount = (result.errors || []).length;
      if (errCount > 0) {
        setBanner({ kind: 'error', text: `Queued ${queued} pull${queued === 1 ? '' : 's'}, ${errCount} failed (agent may be unreachable).` });
      } else {
        setBanner({ kind: 'success', text: `Queued ${queued} job-cost pull${queued === 1 ? '' : 's'}.` });
      }
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || 'Failed to queue job-cost pulls.' });
    } finally {
      setPulling(false);
    }
  };

  const handlePullOne = async (job) => {
    if (pullingId || pulling) return;
    setPullingId(job.id);
    setBanner(null);
    try {
      await pullJobCostRosterOne(job.id);
      await fetchRoster();
      setBanner({ kind: 'success', text: `Queued a job-cost pull for ${job.job_number}.` });
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || `Failed to queue the pull for ${job.job_number}.` });
    } finally {
      setPullingId(null);
    }
  };

  const fetchBackfill = useCallback(async (silent = false) => {
    // `silent` refetches (after edits) must NOT flip bfLoading — that would
    // swap the table for the spinner and unmount the field being typed in.
    if (!silent) setBfLoading(true);
    try {
      const data = await getJobCostBackfill();
      setBfJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message });
    } finally {
      if (!silent) setBfLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === 'backfill') fetchBackfill(); }, [tab, fetchBackfill]);

  const handleSetStart = async (job, month) => {
    if (!/^\d{4}-\d{2}$/.test(month || '') || month === (job.start_month || '')) return;
    setBfJobs((prev) => prev.map((j) => (j.job_number === job.job_number ? { ...j, start_month: month } : j)));
    try {
      await setJobCostBackfillStart(job.job_number, month);
      await fetchBackfill(true); // silent — don't unmount the row being edited
    } catch (e) {
      setBanner({ kind: 'error', text: e.message });
    }
  };

  const handleBackfill = async (job) => {
    if (bfBusy) return;
    setBfBusy(job.job_number);
    setBanner(null);
    try {
      const res = await runJobCostBackfill(job.job_number, job.start_month);
      const q = res.queued || 0;
      const errCount = (res.errors || []).length;
      if (errCount > 0) {
        setBanner({ kind: 'error', text: `Queued ${q} month${q === 1 ? '' : 's'} for ${job.job_number}, ${errCount} failed (agent may be unreachable).` });
      } else {
        setBanner({ kind: 'success', text: `Queued ${q} month${q === 1 ? '' : 's'} of history for ${job.job_number}. They fill in as the agent pulls each — re-run anytime to catch gaps.` });
      }
      await fetchBackfill();
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || 'Failed to queue backfill.' });
    } finally {
      setBfBusy(null);
    }
  };

  if (loading) return <LoadingState message="Loading job cost…" padding="60px 20px" />;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      {/* Section header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 style={{ margin: 0, fontSize: 17.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.015em' }}>Job Cost</h2>
          <p style={{ margin: '4px 0 0', fontSize: 14.5, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty' }}>{tab === 'roster'
            ? 'CE-integrated projects (plus any manual jobs) to pull job cost for. Pulls are inception-to-date — schedule them or pull now.'
            : 'Import historical monthly cost for in-flight projects — one pull per past month, so the cost trend has history.'}</p>
        </div>
        {tab === 'roster' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button type="button" className="s-primary-soft" onClick={() => { setBanner(null); setConfirmPull(true); }} disabled={pulling || enabledCount === 0} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px 0 12px', borderRadius: 8,
              fontSize: 14, fontWeight: 600, color: 'var(--accent-text)', whiteSpace: 'nowrap',
              background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border-default))', boxShadow: 'var(--shadow-xs)',
              cursor: (pulling || enabledCount === 0) ? 'default' : 'pointer', opacity: (pulling || enabledCount === 0) ? 0.6 : 1,
            }}>
              {pulling ? <StIcon name="spin" size={14} sw={2.2} /> : <StIcon name="refresh" size={14} sw={1.9} />}
              {pulling ? 'Queuing…' : 'Pull job cost now'}
            </button>
            <button type="button" className="s-primary" onClick={() => { setBanner(null); setModalOpen(true); }} style={primaryBtn}>
              <StIcon name="plus" size={14} sw={2.2} />Add Job
            </button>
          </div>
        )}
      </div>

      <JcTabs value={tab} onChange={(t) => { setBanner(null); setTab(t); }} />

      {banner && <Banner kind={banner.kind} onDismiss={() => setBanner(null)}>{banner.text}</Banner>}

      {tab === 'roster' ? (
      <>
      {/* Scheduled pull cadence */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '14px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-xs)' }}>
        <span style={{ width: 34, height: 34, borderRadius: 8, flex: '0 0 auto', color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <StIcon name="clock" size={17} sw={1.7} />
        </span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>Scheduled pull</div>
          <div style={{ fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.45, textWrap: 'pretty' }}>Automatically pull job cost for all enabled jobs on this cadence.</div>
        </div>
        <CadenceSelector value={cadence} busy={savingCadence} onChange={handleCadence} />
      </div>

      {/* Roster table */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '13px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-4)' }}>Roster</span>
          <span className="mono" style={{ minWidth: 18, height: 18, padding: '0 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>{jobs.length}</span>
          {enabledCount !== jobs.length && (
            <span style={{ fontSize: 12.5, color: 'var(--text-4)' }}>· {enabledCount} enabled</span>
          )}
        </div>
        <RosterTable jobs={jobs} onToggle={handleToggle} onRemove={(j) => { setBanner(null); setConfirmJob(j); }} onPull={handlePullOne} togglingId={togglingId} removingId={removingId} pullingId={pullingId} />
      </div>
      <ArchivedRosterSection
        jobs={archivedJobs}
        open={archivedOpen}
        onToggle={() => setArchivedOpen((o) => !o)}
        onRestore={handleRestoreArchived}
        onDelete={(j) => { setBanner(null); setConfirmArchDelete(j); }}
        busyId={archBusyId} />
      </>
      ) : (
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '13px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-4)' }}>Projects</span>
          <span className="mono" style={{ minWidth: 18, height: 18, padding: '0 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>{bfJobs.length}</span>
        </div>
        {bfLoading ? <LoadingState message="Loading backfill status…" padding="40px 20px" /> : <BackfillTable jobs={bfJobs} onSetStart={handleSetStart} onBackfill={handleBackfill} busyNumber={bfBusy} />}
      </div>
      )}

      {modalOpen && (
        <AddRosterJobModal existingNumbers={jobs.map((j) => j.job_number)} onClose={() => setModalOpen(false)} onSubmit={handleAddJob} />
      )}
      {confirmJob && (
        <ArchiveJobModal
          job={confirmJob}
          onClose={() => { if (!removingId) setConfirmJob(null); }}
          onArchive={handleArchive}
          onDelete={handleDelete} />
      )}
      {confirmArchDelete && (
        <ConfirmDialog
          title="Delete this job permanently?"
          body={<>Permanently delete <strong className="mono" style={{ color: 'var(--text-1)', fontWeight: 600 }}>{confirmArchDelete.job_number}</strong> and purge its cost data? This can't be undone.</>}
          busy={archBusyId === confirmArchDelete.id}
          confirmLabel="Delete permanently"
          busyLabel="Deleting…"
          onCancel={() => { if (!archBusyId) setConfirmArchDelete(null); }}
          onConfirm={handleDeleteArchived} />
      )}
      {confirmPull && (
        <ConfirmDialog
          title="Pull job cost now?"
          body={<>Queue a job-cost pull for all <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{enabledCount}</strong> enabled job{enabledCount === 1 ? '' : 's'}? The CE-Agent runs each as an inception-to-date report.</>}
          busy={pulling} onCancel={() => { if (!pulling) setConfirmPull(false); }} onConfirm={handlePullAll}
          confirmKind="primary" icon="refresh" confirmLabel="Pull now" busyLabel="Queuing…" />
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   AR Sync section — scheduler cadence + pull-now + status + backfill.
   Mirrors Job Cost, minus the roster (AR reports are company-wide).
   ═══════════════════════════════════════════════════════════════════════════ */
const arMoney = (v) => (v == null ? '—' : Number(v).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }));

const ArStatusCard = ({ label, primary, sub }) => (
  <div style={{ flex: '1 1 240px', minWidth: 240, background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-xs)', padding: '14px 16px' }}>
    <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' }}>{label}</div>
    <div className="mono" style={{ fontSize: 18, fontWeight: 700, color: 'var(--text-1)', marginTop: 6, letterSpacing: '-0.02em' }}>{primary}</div>
    <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 3, lineHeight: 1.45 }}>{sub}</div>
  </div>
);

const ArSyncSection = () => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [backfill, setBackfill] = useState(null);
  const [banner, setBanner] = useState(null);
  const [savingCadence, setSavingCadence] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [bfStart, setBfStart] = useState('');
  const [bfBusy, setBfBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [s, bf] = await Promise.all([getArSyncSettings(), getArSyncBackfill().catch(() => null)]);
      setData(s);
      setBackfill(bf);
      if (bf && bf.start_month) setBfStart(bf.start_month);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const handleCadence = async (c) => {
    setSavingCadence(true); setBanner(null);
    try { await setArSyncCadence(c); setData((d) => ({ ...d, cadence: c })); }
    catch (e) { setBanner({ kind: 'error', text: e.message }); }
    finally { setSavingCadence(false); }
  };

  const handlePull = async () => {
    if (pulling) return;
    setPulling(true); setBanner(null);
    try {
      const r = await pullArSyncNow(false);
      const failed = (r.errors || []).length;
      setBanner({ kind: failed ? 'error' : 'success', text: `Queued ${r.queued} AR pull${r.queued === 1 ? '' : 's'}${failed ? `, ${failed} failed (agent may be unreachable)` : ' — they land in a few minutes'}.` });
      setTimeout(load, 1500);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || 'Failed to queue AR pulls.' });
    } finally { setPulling(false); }
  };

  const handleBackfill = async () => {
    if (bfBusy) return;
    if (!/^\d{4}-\d{2}$/.test(bfStart)) { setBanner({ kind: 'error', text: 'Enter a start month (YYYY-MM).' }); return; }
    setBfBusy(true); setBanner(null);
    try {
      const r = await runArSyncBackfill(bfStart);
      const cq = (r.collections && r.collections.queued) || 0;
      const aq = (r.aging && r.aging.queued) || 0;
      setBanner({ kind: 'success', text: `Backfill queued: ${cq} collections + ${aq} aging month(s) across a ${r.months}-month range. They fill in as the agent pulls each — re-run anytime to catch gaps.` });
      setTimeout(load, 1500);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message || 'Failed to queue backfill.' });
    } finally { setBfBusy(false); }
  };

  if (loading) return <LoadingState message="Loading AR sync…" padding="60px 20px" />;

  // A failed settings load leaves data=null; render a recoverable error rather
  // than dereferencing null below (which would crash the whole Settings page).
  if (!data) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {banner && <Banner kind={banner.kind} onDismiss={() => setBanner(null)}>{banner.text}</Banner>}
        <div style={{ padding: '28px 20px', textAlign: 'center', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-xs)' }}>
          <div style={{ fontSize: 14.5, color: 'var(--text-2)', marginBottom: 12 }}>Couldn’t load AR sync settings.</div>
          <button type="button" className="s-primary-soft" onClick={() => { setBanner(null); setLoading(true); load(); }} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px', borderRadius: 8,
            fontSize: 14, fontWeight: 600, color: 'var(--accent-text)',
            background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border-default))', boxShadow: 'var(--shadow-xs)', cursor: 'pointer',
          }}>
            <StIcon name="refresh" size={14} sw={1.9} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 260 }}>
          <h2 style={{ margin: 0, fontSize: 17.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.015em' }}>AR Sync</h2>
          <p style={{ margin: '4px 0 0', fontSize: 14.5, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty' }}>
            Mirror ComputerEase receivables into Summit — the outstanding aging snapshot and monthly collections. Schedule it or pull now.
          </p>
        </div>
        <button type="button" className="s-primary-soft" onClick={handlePull} disabled={pulling} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px 0 12px', borderRadius: 8,
          fontSize: 14, fontWeight: 600, color: 'var(--accent-text)', whiteSpace: 'nowrap',
          background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border-default))', boxShadow: 'var(--shadow-xs)',
          cursor: pulling ? 'default' : 'pointer', opacity: pulling ? 0.6 : 1,
        }}>
          {pulling ? <StIcon name="spin" size={14} sw={2.2} /> : <StIcon name="refresh" size={14} sw={1.9} />}
          {pulling ? 'Queuing…' : 'Pull AR now'}
        </button>
      </div>

      {banner && <Banner kind={banner.kind} onDismiss={() => setBanner(null)}>{banner.text}</Banner>}

      {/* Scheduled pull cadence */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '14px 16px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-xs)' }}>
        <span style={{ width: 34, height: 34, borderRadius: 8, flex: '0 0 auto', color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
          <StIcon name="clock" size={17} sw={1.7} />
        </span>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-1)' }}>Scheduled pull</div>
          <div style={{ fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.45, textWrap: 'pretty' }}>Automatically pull the aging snapshot + this month’s collections on this cadence. Weekly also re-pulls the last two months (late applications).</div>
        </div>
        <CadenceSelector value={data.cadence} busy={savingCadence} onChange={handleCadence} />
      </div>

      {/* Status */}
      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <ArStatusCard
          label="Outstanding (aging)"
          primary={data.aging ? arMoney(data.aging.total_balance) : '—'}
          sub={data.aging ? `snapshot ${fmtDate(data.aging.report_date)} · ${data.aging.rows} invoices` : 'No snapshot pulled yet'}
        />
        <ArStatusCard
          label="Collections"
          primary={data.collections ? (data.collections.last_period || '—') : '—'}
          sub={data.collections ? `latest month · last pulled ${fmtDate(data.collections.last_pulled)}` : 'No collections pulled yet'}
        />
      </div>
      {data.last_run && data.last_run.at && (
        <div style={{ fontSize: 12.5, color: 'var(--text-4)', marginTop: -6 }}>
          Last pull: {fmtDateTime(data.last_run.at)} · {data.last_run.by} · queued {data.last_run.queued}{data.last_run.errors ? `, ${data.last_run.errors} failed` : ''}
        </div>
      )}

      {/* Backfill */}
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '13px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-4)' }}>Historical backfill</span>
        </div>
        <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty' }}>
            Import past months of AR so the mirror spans history — one collections pull + one aging month-end snapshot per month, from the start month through last closed month. Months already present are skipped, so re-running fills gaps.
          </p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13.5, fontWeight: 500, color: 'var(--text-3)' }}>Start month</label>
            <input type="month" value={bfStart} onChange={(e) => setBfStart(e.target.value)} style={{
              height: 34, padding: '0 10px', borderRadius: 8, fontSize: 14, color: 'var(--text-1)',
              background: 'var(--bg-surface)', border: '1px solid var(--border-default)', fontFamily: 'var(--font-sans)',
            }} />
            <button type="button" className="s-primary-soft" onClick={handleBackfill} disabled={bfBusy} style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 14px', borderRadius: 8,
              fontSize: 14, fontWeight: 600, color: 'var(--accent-text)', whiteSpace: 'nowrap',
              background: 'var(--bg-surface)', border: '1px solid color-mix(in oklch, var(--accent) 30%, var(--border-default))', boxShadow: 'var(--shadow-xs)',
              cursor: bfBusy ? 'default' : 'pointer', opacity: bfBusy ? 0.6 : 1,
            }}>
              {bfBusy ? <StIcon name="spin" size={14} sw={2.2} /> : <StIcon name="archive" size={14} sw={1.7} />}
              {bfBusy ? 'Queuing…' : 'Run backfill'}
            </button>
          </div>
          {backfill && backfill.configured && (
            <div className="mono" style={{ fontSize: 12.5, color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>
              Collections {backfill.collections_done}/{backfill.expected_months} · Aging {backfill.aging_done}/{backfill.expected_months} months mirrored
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   Page
   ═══════════════════════════════════════════════════════════════════════════ */
const SettingsPage = ({ currentUser }) => {
  const [activeSection, setActiveSection] = useState('notification-lists');

  const [lists, setLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [membersLoading, setMembersLoading] = useState(false);

  const [modal, setModal] = useState(null); // null | 'new-list' | 'add-member'
  const [confirmMember, setConfirmMember] = useState(null);
  const [banner, setBanner] = useState(null); // null | { kind, text }
  const [removingMemberId, setRemovingMemberId] = useState(null);
  const [togglingListId, setTogglingListId] = useState(null);

  const fetchLists = useCallback(async () => {
    const data = await getNotificationLists();
    const arr = Array.isArray(data) ? data : [];
    setLists(arr);
    // Auto-select mission-critical (else first) only when nothing is selected.
    setSelectedListId((prev) => {
      if (prev && arr.some((l) => l.id === prev)) return prev;
      if (arr.length === 0) return null;
      const mc = arr.find((l) => l.list_name === 'mission-critical');
      return (mc || arr[0]).id;
    });
    return arr;
  }, []);

  const fetchMembers = useCallback(async (listId) => {
    try {
      setMembersLoading(true);
      const data = await getListMembers(listId);
      setMembers(Array.isArray(data) ? data : []);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message });
    } finally {
      setMembersLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try { await fetchLists(); }
      catch (e) { setBanner({ kind: 'error', text: e.message }); }
      finally { setLoading(false); }
    })();
  }, [fetchLists]);

  useEffect(() => {
    if (selectedListId) fetchMembers(selectedListId);
    else setMembers([]);
  }, [selectedListId, fetchMembers]);

  const selectedList = lists.find((l) => l.id === selectedListId) || null;

  const handleCreateList = async (data) => {
    await createNotificationList(data);
    const fresh = await fetchLists();
    const created = fresh.find((l) => l.list_name === data.list_name);
    if (created) setSelectedListId(created.id);
    setModal(null);
    setBanner({ kind: 'success', text: `List "${data.list_name}" created.` });
  };

  const handleAddMember = async (data) => {
    if (!selectedList) return;
    await addListMember(selectedList.id, data);
    await Promise.all([fetchMembers(selectedList.id), fetchLists()]);
    setModal(null);
    setBanner({ kind: 'success', text: `Added ${data.email}.` });
  };

  const confirmRemoveMember = async () => {
    if (!confirmMember || !selectedList || removingMemberId) return;
    try {
      setRemovingMemberId(confirmMember.id);
      await removeListMember(selectedList.id, confirmMember.id);
      await Promise.all([fetchMembers(selectedList.id), fetchLists()]);
      setBanner({ kind: 'success', text: `Removed ${confirmMember.email}.` });
      setConfirmMember(null);
    } catch (e) {
      setBanner({ kind: 'error', text: e.message });
    } finally {
      setRemovingMemberId(null);
    }
  };

  const handleToggleList = async () => {
    if (!selectedList || togglingListId) return;
    try {
      setTogglingListId(selectedList.id);
      await updateNotificationList(selectedList.id, { is_active: selectedList.is_active ? 0 : 1 });
      await fetchLists();
      setBanner({ kind: 'success', text: `List "${selectedList.list_name}" ${selectedList.is_active ? 'disabled' : 'enabled'}.` });
    } catch (e) {
      setBanner({ kind: 'error', text: e.message });
    } finally {
      setTogglingListId(null);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
      <style>{`
        @keyframes sSpin { to { transform: rotate(360deg); } }
        .s-rail-item:hover { background: var(--bg-hover); }
        .s-listcard:hover { border-color: var(--border-strong); background: var(--bg-hover); }
        .s-row:hover td { background: var(--bg-hover); }
        .s-trash:hover { color: var(--danger); background: var(--danger-soft); border-color: color-mix(in oklch, var(--danger) 22%, transparent); }
        .s-archive:hover { color: var(--accent-text); background: var(--accent-soft); border-color: color-mix(in oklch, var(--accent) 22%, transparent); }
        .s-rowpull:hover:not(:disabled) { color: var(--accent-text); background: var(--accent-soft); border-color: color-mix(in oklch, var(--accent) 22%, transparent); }
        .s-danger-ghost:hover:not(:disabled) { background: var(--danger-soft); border-color: color-mix(in oklch, var(--danger) 45%, var(--border-default)); }
        .s-toggle-off:hover { background: var(--warning-soft); }
        .s-toggle-on:hover { background: var(--accent-soft); }
        .s-primary:hover { background: var(--accent-hover); }
        .s-primary-soft:hover:not(:disabled) { background: var(--accent-soft); }
        .s-enabled-pill:hover:not(:disabled) { filter: brightness(0.97); }
        .s-modal-x:hover { background: var(--bg-hover); color: var(--text-2); }
        .s-banner-x:hover { filter: brightness(0.95); }
      `}</style>

      <SectionRail sections={SETTINGS_SECTIONS} active={activeSection} onSelect={setActiveSection} />

      {activeSection === 'job-cost-roster' ? (
        <div style={{ flex: 1, minWidth: 0 }}>
          <JobCostRosterSection />
        </div>
      ) : activeSection === 'ar-sync' ? (
        <div style={{ flex: 1, minWidth: 0 }}>
          <ArSyncSection />
        </div>
      ) : loading ? (
        <div style={{ flex: 1, minWidth: 0 }}>
          <LoadingState message="Loading settings…" padding="80px 20px" />
        </div>
      ) : (
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
        {/* Section header */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <h2 style={{ margin: 0, fontSize: 17.5, fontWeight: 600, color: 'var(--text-1)', letterSpacing: '-0.015em' }}>Notification Lists</h2>
            <p style={{ margin: '4px 0 0', fontSize: 14.5, color: 'var(--text-3)', lineHeight: 1.5, textWrap: 'pretty' }}>Manage email recipient lists for automated alerts and notifications.</p>
          </div>
          <button type="button" className="s-primary" onClick={() => { setBanner(null); setModal('new-list'); }} style={primaryBtn}>
            <StIcon name="plus" size={14} sw={2.2} />New List
          </button>
        </div>

        {banner && <Banner kind={banner.kind} onDismiss={() => setBanner(null)}>{banner.text}</Banner>}

        {lists.length === 0 ? (
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-sm)' }}>
            <StateScreen kind="empty" title="No notification lists yet"
              body="Create your first recipient list to start routing automated alerts and notifications."
              primary={{ label: 'New List', onClick: () => { setBanner(null); setModal('new-list'); }, icon: 'plus' }} />
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
            {/* Lists panel */}
            <div style={{ width: 300, flex: '0 0 300px', alignSelf: 'flex-start', display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 2px' }}>
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-4)' }}>All Lists</span>
                <span className="mono" style={{ minWidth: 18, height: 18, padding: '0 6px', borderRadius: 999, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, fontVariantNumeric: 'tabular-nums', color: 'var(--text-3)', background: 'var(--bg-surface-2)', border: '1px solid var(--border-subtle)' }}>{lists.length}</span>
              </div>
              {lists.map((l) => (
                <ListCard key={l.id} list={l} selected={l.id === selectedListId} onSelect={(id) => { setSelectedListId(id); setBanner(null); }} />
              ))}
            </div>

            {/* Members panel */}
            {selectedList ? (
              <MembersPanel
                list={selectedList} members={members} loading={membersLoading}
                onAddMember={() => { setBanner(null); setModal('add-member'); }}
                onRemoveMember={(m) => { setBanner(null); setConfirmMember(m); }}
                removingId={removingMemberId}
                onToggle={handleToggleList} toggling={togglingListId === selectedList.id}
              />
            ) : (
              <div style={{ flex: 1, minWidth: 0, alignSelf: 'flex-start', minHeight: 300, background: 'var(--bg-surface)', border: '1px dashed var(--border-default)', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ fontSize: 14.5, color: 'var(--text-4)' }}>Select a list to manage its members.</span>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {modal === 'new-list' && (
        <NewListModal existingNames={lists.map((l) => l.list_name)} onClose={() => setModal(null)} onSubmit={handleCreateList} />
      )}
      {modal === 'add-member' && selectedList && (
        <AddMemberModal
          listName={selectedList.list_name}
          existingEmails={members.filter((m) => m.is_active).map((m) => m.email)}
          onClose={() => setModal(null)} onSubmit={handleAddMember} />
      )}
      {confirmMember && (
        <ConfirmDialog
          title="Remove this member?"
          body={<>Remove <strong className="mono" style={{ color: 'var(--text-1)', fontWeight: 600 }}>{confirmMember.email}</strong> from <strong style={{ color: 'var(--text-1)', fontWeight: 600 }}>{selectedList?.list_name}</strong>? They’ll stop receiving this list’s notifications.</>}
          busy={!!removingMemberId} onCancel={() => { if (!removingMemberId) setConfirmMember(null); }} onConfirm={confirmRemoveMember} />
      )}
    </div>
  );
};

export default SettingsPage;
